import { useMutation } from "@tanstack/react-query";
import moment from "moment";
import { useNewton } from "../store/NewtonContext";
import { useUserData } from "../../../../../hooks/useUserData";
import { llmService } from "../services/llmService";
import { uploadVideoAndGetURL, uploadFileAndGetGsUrl } from "../services/storageService";
import { fileToFileDataPart } from "../utils/file";
import { extractLastResultStream } from "../utils/sse";
import { cleanIntrinsic } from "../utils/cleanIntrinsic";
import { convertHeightToInches } from "../../../utils/convertHeightToInches";

const createVideoAnalysisMetadata = (videoMetadata) => ({
  clubId: videoMetadata.club,
  fps: videoMetadata.fps,
  gender: videoMetadata.gender,
  heightInInches: videoMetadata.heightInInches,
  intrinsic: [1300.0, 757.0, 1300.0, 540.0, 1.0],
  swingAngle: videoMetadata.swingAngle,
  swingIsLeftHanded: videoMetadata.swingIsLeftHanded,
});

export const useAskQuestion = () => {
  const { llmEnv, sessionId, setMessagesHistory } = useNewton();
  const { userData } = useUserData();

  return useMutation({
    mutationFn: async ({
      attachedFiles,
      selectedLibraryVideo,
      userMsg,
      videoFile,
      videoMetadata,
    }) => {
      if (!userData?.uid) {
        throw new Error("User not authenticated");
      }
      
      if (!sessionId) {
        throw new Error("Session not initialized");
      }

      if (selectedLibraryVideo) {
        // Use video from library - extract metadata directly from video object
        const { metaData, userData: videoUserData } = selectedLibraryVideo;

        const metadataMap = {
          clubId: videoUserData.golfClub,
          fps: metaData.fps,
          gender: videoUserData.gender?.toUpperCase() || "",
          heightInInches: convertHeightToInches(videoUserData?.height),
          intrinsic: cleanIntrinsic(metaData.intrinsic || []),
          swingAngle: metaData.cameraAngle,
          swingIsLeftHanded: videoUserData.hand?.toLowerCase() === "left",
        };

        // Convert HTTPS URL to gs:// format if needed
        const videoUrl = selectedLibraryVideo.videoPath;

        return llmService.analyzeVideoQuery({
          env: llmEnv,
          metadataMap,
          query: userMsg,
          sessionId,
          userId: userData.uid,
          videoId: selectedLibraryVideo.id,
          videoIsAnalyzed: selectedLibraryVideo.analyzed || false,
          videoUrl,
        });
      } else if (videoFile) {
        // Upload and use local video file
        const gsUrl = await uploadVideoAndGetURL(videoFile, userData.uid);
        const metadataMap = createVideoAnalysisMetadata(videoMetadata);

        return llmService.analyzeVideoQuery({
          env: llmEnv,
          metadataMap,
          query: userMsg || "How's my swing?",
          sessionId,
          userId: userData.uid,
          videoUrl: gsUrl,
        });
      } else {
        let fileParts = [];
        if (attachedFiles?.length) {
          fileParts = await Promise.all(
            attachedFiles.map(async (file) => {
              const gsUri = await uploadFileAndGetGsUrl(file, userData.uid, sessionId);
              return fileToFileDataPart(file, gsUri);
            })
          );
        }

        return llmService.readQueryStream({
          fileParts,
          env: llmEnv,
          userId: userData.uid,
          sessionId,
          userMsg,
        });
      }
    },
    onError: (err) => {
      console.error("useAskQuestion error:", err);

      setMessagesHistory((prev) =>
        prev.map((m) =>
          m.pending
            ? {
                ...m,
                message: err.message || "Network error. Please try again.",
                pending: false,
                timestamp: moment().valueOf(),
              }
            : m
        )
      );
    },
    onMutate: async ({
      attachedFiles,
      selectedLibraryVideo,
      userMsg,
      videoFile,
    }) => {
      setMessagesHistory((prev) => [
        ...prev,
        {
          attachedFilesLength:
            videoFile || selectedLibraryVideo ? 1 : attachedFiles.length,
          from: "user",
          message: userMsg,
          pending: false,
          timestamp: moment().valueOf(),
        },
        {
          from: "assistant",
          message: "",
          pending: true,
          timestamp: moment().valueOf(),
        },
      ]);
    },
    onSuccess: async (res) => {
      if (res.body) {
        const result = await extractLastResultStream(res.body);
        if (!result) {
          throw new Error("Failed to parse response");
        }
        const { text, timestampMs } = result;
        setMessagesHistory((prev) =>
          prev.map((m) =>
            m.pending
              ? {
                  ...m,
                  message: text || "…",
                  pending: false,
                  timestamp: timestampMs || moment().valueOf(),
                }
              : m
          )
        );
      } else {
        throw new Error("Something went wrong");
      }
    },
  });
};
